import type {
  ExaminerTurn,
  ExaminerTurnProgress,
  SpeakingMode,
  SpeakingPart,
  SpeakingPhase,
} from '@ielts-buddy/shared'
import type React from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  finishSpeakingSession,
  skipSpeakingTurn,
  startSpeakingSession,
  submitSpeakingTurn,
} from '../../shared/api.js'
import { useRouter } from '../../shared/router-store.js'
import { haptics, showConfirm } from '../../shared/telegram.js'
import { useAudioRecorder } from './use-audio-recorder.js'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ClientPhase =
  | { kind: 'starting' }
  | { kind: 'examiner_speaking'; turn: ExaminerTurn }
  | { kind: 'awaiting_answer'; turn: ExaminerTurn; serverPhase: SpeakingPhase }
  | { kind: 'cue_card_prep'; turn: ExaminerTurn; secondsLeft: number }
  | { kind: 'recording'; turn: ExaminerTurn }
  | { kind: 'uploading'; turn: ExaminerTurn }
  | { kind: 'turn_error'; turn: ExaminerTurn; message: string }
  | { kind: 'finishing' }
  | { kind: 'error'; message: string }

// ---------------------------------------------------------------------------
// Constants (exported so the screen can reference them in JSX)
// ---------------------------------------------------------------------------

export const CUE_CARD_PREP_SECONDS = 60

// Maximum recording length per IELTS Speaking part, in seconds. These mirror
// the real IELTS examiner behaviour:
//   - Part 1 (4-5 min total / 8-10 questions): typical answers 15-30s, the
//     examiner moves on around the 30s mark. We give 45s of headroom so a
//     candidate who's mid-sentence isn't cut off.
//   - Part 2 (cue card monologue): IELTS rule is "1 to 2 minutes" with a HARD
//     stop at 2:00 — the examiner literally interrupts. We honour that.
//   - Part 3 (discussion): answers run 45-75s, examiner cuts at ~75-90s. We
//     give the longer cap because Part 3 rewards developed responses.
// When the cap is hit we auto-stop the recorder and submit, so the candidate
// never loses an answer to a server-side truncation.
export const PART_RECORDING_CAP_S: Record<1 | 2 | 3, number> = {
  1: 45,
  2: 120,
  3: 90,
}

// Seconds before the cap at which we fire a "wrap up" haptic. Real IELTS
// examiners visibly glance at their stopwatch around this point — this is the
// digital equivalent.
export const WRAP_UP_WARNING_S = 10

// ---------------------------------------------------------------------------
// Hook return type
// ---------------------------------------------------------------------------

export interface SpeakingSessionHook {
  phase: ClientPhase
  sessionId: string | null
  lastTranscript: string | null
  partProgress: ExaminerTurnProgress | null
  recordingElapsed: number
  recordingCap: number
  showExaminerText: boolean
  recorderVolume: number
  navigateHome: () => void
  startRecording(): Promise<void>
  stopAndUpload(): Promise<void>
  retryTurn(): void
  skipTurn(): Promise<void>
  handleClose(): Promise<void>
  setShowExaminerText: React.Dispatch<React.SetStateAction<boolean>>
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

interface UseSpeakingSessionParams {
  mode: SpeakingMode
  part?: SpeakingPart
}

export function useSpeakingSession({
  mode,
  part,
}: UseSpeakingSessionParams): SpeakingSessionHook {
  const navigateHome = useRouter((s) => s.navigateHome)
  const navigateToSpeakingResults = useRouter(
    (s) => s.navigateToSpeakingResults,
  )
  const recorder = useAudioRecorder()

  const [phase, setPhase] = useState<ClientPhase>({ kind: 'starting' })
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [lastTranscript, setLastTranscript] = useState<string | null>(null)
  // Authoritative, server-computed progress for the segmented Part 1 / 2 / 3
  // bar. Updated each time we enter a new examiner turn — see enterTurn.
  // Null until the first turn arrives (initial loading state).
  const [partProgress, setPartProgress] = useState<ExaminerTurnProgress | null>(
    null,
  )

  const audioElRef = useRef<HTMLAudioElement | null>(null)
  const cueCardTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  // Always-current reference to stopAndUpload, used by the recording-cap timer
  // to avoid a stale-closure bug: the interval callback is created inside
  // startRecording (phase = awaiting_answer) but must call the stopAndUpload
  // that has phase = recording. Without this ref, the stale version's
  // `if (phase.kind !== 'recording') return` guard fires and the recording
  // never auto-stops at the cap.
  const stopAndUploadRef = useRef<() => Promise<void>>(async () => {})
  const recordingStartRef = useRef<number>(0)
  // Live elapsed-seconds counter for the on-screen recording meter. Updated
  // ~4× / sec while recording so the mm:ss display feels responsive without
  // re-rendering on every animation frame.
  const [recordingElapsed, setRecordingElapsed] = useState(0)
  const [recordingCap, setRecordingCap] = useState(0)
  // Has the "wrap up in 10s" haptic fired yet for this turn? Reset on each
  // startRecording so we don't double-fire if the timer interval ticks twice
  // inside the warning window.
  const wrapUpFiredRef = useRef(false)
  // Guard against React StrictMode double-mount in dev. The bootstrap effect
  // runs twice (setup → cleanup → setup) and without this we'd create two
  // backend sessions, doubling our OpenAI bill on every page load.
  const bootedRef = useRef(false)
  // True once the screen has been torn down for real (not just StrictMode's
  // dev double-mount). Used to short-circuit any audio playback that the
  // bootstrap promise might try to start AFTER the user has navigated away —
  // otherwise the examiner keeps talking from off-screen.
  const unmountedRef = useRef(false)
  // Whether the user has manually revealed the examiner's text (e.g. they
  // couldn't catch what was said). Hidden by default — the audio is the
  // primary channel.
  const [showExaminerText, setShowExaminerText] = useState(false)

  // ---------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------

  const playExaminerAudio = useCallback(
    (turn: ExaminerTurn): Promise<void> =>
      new Promise((resolve) => {
        // The bootstrap promise can resolve AFTER the user has left the
        // screen (TTS generation is 2–5s). Don't start a new Audio in that
        // case or the examiner will keep talking from a dead component.
        if (unmountedRef.current) {
          resolve()
          return
        }
        const audio = new Audio(
          `data:${turn.audioMimeType};base64,${turn.audioBase64}`,
        )
        audioElRef.current = audio
        audio.onended = () => resolve()
        audio.onerror = () => resolve() // never block the test on audio failures
        // Some browsers reject autoplay; if so, surface a tap-to-listen UX.
        audio.play().catch(() => resolve())
      }),
    [],
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: finishSession is intentionally excluded — it's defined after enterTurn and including it would create a circular dep chain
  const enterTurn = useCallback(
    async (turn: ExaminerTurn, serverPhase: SpeakingPhase) => {
      // Snap the segmented bar to wherever the server says we are. Doing this
      // once here (instead of in every caller — bootstrap, /turn, /skip-turn)
      // means the bar never desyncs with the actual conversation, which is
      // what caused the "Part 2 cue card resets to Part 1" glitch.
      setPartProgress(turn.progress)
      setPhase({ kind: 'examiner_speaking', turn })
      await playExaminerAudio(turn)

      if (serverPhase === 'cue_card_prep') {
        let secondsLeft = CUE_CARD_PREP_SECONDS
        setPhase({ kind: 'cue_card_prep', turn, secondsLeft })
        if (cueCardTimerRef.current) clearInterval(cueCardTimerRef.current)
        cueCardTimerRef.current = setInterval(() => {
          secondsLeft -= 1
          if (secondsLeft <= 0) {
            if (cueCardTimerRef.current) clearInterval(cueCardTimerRef.current)
            cueCardTimerRef.current = null
            setPhase({
              kind: 'awaiting_answer',
              turn,
              serverPhase: 'candidate_speaking',
            })
          } else {
            setPhase({ kind: 'cue_card_prep', turn, secondsLeft })
          }
        }, 1000)
      } else if (serverPhase === 'finished') {
        // Defensive: server shouldn't send 'finished' here, but in case
        await finishSession()
      } else {
        setPhase({ kind: 'awaiting_answer', turn, serverPhase })
      }
    },
    [playExaminerAudio],
  )

  const finishSession = useCallback(async () => {
    if (!sessionId) return
    setPhase({ kind: 'finishing' })
    try {
      await finishSpeakingSession(sessionId)
      haptics.success()
      navigateToSpeakingResults(sessionId)
    } catch (err) {
      haptics.error()
      setPhase({ kind: 'error', message: (err as Error).message })
    }
  }, [sessionId, navigateToSpeakingResults])

  const startRecording = useCallback(async () => {
    if (phase.kind !== 'awaiting_answer' && phase.kind !== 'cue_card_prep')
      return
    const turn = phase.turn
    // Tear down the cue-card prep countdown if it's still ticking, otherwise
    // it'll fire one second later and stomp our 'recording' phase with a
    // stale 'cue_card_prep' update — making the mic appear to stop on its own.
    if (cueCardTimerRef.current) {
      clearInterval(cueCardTimerRef.current)
      cueCardTimerRef.current = null
    }
    try {
      await recorder.start()
      haptics.medium()
      recordingStartRef.current = Date.now()
      // Reset the per-turn timer state. Crucial: without this, a long Part 3
      // answer would still show the warning fired-flag from a previous Part 1
      // turn and never re-fire.
      setRecordingElapsed(0)
      wrapUpFiredRef.current = false
      setPhase({ kind: 'recording', turn })

      const partCap = PART_RECORDING_CAP_S[turn.part]
      const cap =
        turn.expectedAnswerSeconds > 0 && turn.expectedAnswerSeconds < partCap
          ? turn.expectedAnswerSeconds
          : partCap
      setRecordingCap(cap)
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current)
      // 250ms cadence — frequent enough that the on-screen mm:ss display
      // updates smoothly, infrequent enough that React isn't re-rendering 60×/s.
      recordingTimerRef.current = setInterval(() => {
        const elapsed = (Date.now() - recordingStartRef.current) / 1000
        setRecordingElapsed(elapsed)
        // Wrap-up nudge: light buzz once, ~10s before the cap. Real IELTS
        // examiners glance at their stopwatch around this point — this is the
        // mobile-app equivalent.
        if (!wrapUpFiredRef.current && elapsed >= cap - WRAP_UP_WARNING_S) {
          wrapUpFiredRef.current = true
          haptics.light()
        }
        if (elapsed >= cap) {
          if (recordingTimerRef.current)
            clearInterval(recordingTimerRef.current)
          recordingTimerRef.current = null
          // Distinct stronger haptic so the user understands they hit the
          // hard limit (not just an arbitrary stop).
          haptics.warning()
          void stopAndUploadRef.current()
        }
      }, 250)
    } catch (err) {
      haptics.error()
      setPhase({ kind: 'error', message: (err as Error).message })
    }
  }, [phase, recorder])

  const stopAndUpload = useCallback(async () => {
    if (recordingTimerRef.current) clearInterval(recordingTimerRef.current)
    recordingTimerRef.current = null

    if (phase.kind !== 'recording') return
    const turn = phase.turn
    haptics.light()
    setPhase({ kind: 'uploading', turn })
    try {
      const blob = await recorder.stop()
      if (!sessionId) throw new Error('No active session')
      const result = await submitSpeakingTurn(sessionId, blob)
      setLastTranscript(result.candidateTranscript)

      if (result.phase === 'finished' || !result.examinerTurn) {
        await finishSession()
        return
      }
      // enterTurn updates partProgress from result.examinerTurn.progress.
      await enterTurn(result.examinerTurn, result.phase)
    } catch (err) {
      // Don't kill the whole session on a single bad upload — the server
      // hasn't advanced past the current turn, so we can let the user
      // re-record this same answer. Anything fatal (auth gone, session
      // not found) will fall through to the catch on the next attempt.
      haptics.error()
      setPhase({ kind: 'turn_error', turn, message: (err as Error).message })
    }
  }, [phase, recorder, sessionId, enterTurn, finishSession])

  // Keep the ref current on every render so the recording-cap timer always
  // calls the version with the up-to-date phase (= 'recording').
  stopAndUploadRef.current = stopAndUpload

  const retryTurn = useCallback(() => {
    if (phase.kind !== 'turn_error') return
    haptics.light()
    setPhase({
      kind: 'awaiting_answer',
      turn: phase.turn,
      serverPhase: 'candidate_speaking',
    })
  }, [phase])

  /**
   * DEV: skip the current question with a canned backend answer. Does not
   * record or upload audio — just bumps the server's script position and
   * jumps straight to the next examiner turn. Faster, cheaper, and
   * tolerates the user being mid-recording (we cancel the recorder first).
   */
  const skipTurn = useCallback(async () => {
    if (!sessionId) return
    haptics.light()
    // If we're recording, abort cleanly so the mic releases.
    if (phase.kind === 'recording') {
      try {
        await recorder.stop()
      } catch {
        recorder.cancel()
      }
    }

    // Use a stable "current turn" snapshot for the uploading visual.
    const turn =
      'turn' in phase && phase.turn ? (phase.turn as ExaminerTurn) : null
    if (turn) setPhase({ kind: 'uploading', turn })

    try {
      const result = await skipSpeakingTurn(sessionId)
      setLastTranscript(result.candidateTranscript)
      if (result.phase === 'finished' || !result.examinerTurn) {
        await finishSession()
        return
      }
      // enterTurn updates partProgress from result.examinerTurn.progress.
      await enterTurn(result.examinerTurn, result.phase)
    } catch (err) {
      haptics.error()
      setPhase({ kind: 'error', message: (err as Error).message })
    }
  }, [phase, recorder, sessionId, enterTurn, finishSession])

  const handleClose = useCallback(async () => {
    // Phases where there's nothing to lose — just leave.
    const safeToLeave =
      phase.kind === 'starting' ||
      phase.kind === 'finishing' ||
      phase.kind === 'error' ||
      phase.kind === 'turn_error'
    if (safeToLeave) {
      haptics.light()
      navigateHome()
      return
    }
    // Mid-test — make the user confirm so they don't lose progress.
    const ok = await showConfirm(
      'Exit the Speaking test? Your progress so far will not be scored.',
    )
    if (ok) {
      haptics.warning()
      navigateHome()
    }
  }, [phase, navigateHome])

  // ---------------------------------------------------------------------
  // Boot
  // ---------------------------------------------------------------------

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional boot-once — guards against StrictMode double-mount; mode/part/enterTurn/recorder are stable for the session lifetime
  useEffect(() => {
    // Reset the unmount flag on every effect run. In StrictMode dev the
    // sequence is mount → cleanup → mount, and the second mount needs the
    // flag to be `false` again so playExaminerAudio actually plays.
    unmountedRef.current = false

    // Boot the session ONCE across the entire screen lifecycle (StrictMode
    // double-mount included), otherwise we'd burn two sessions of OpenAI
    // credit on every page load. The cleanup below runs on EVERY effect
    // teardown though — that's deliberate and how we kill audio properly
    // when the user actually leaves.
    if (!bootedRef.current) {
      bootedRef.current = true
      ;(async () => {
        try {
          const start = await startSpeakingSession({ mode, part })
          setSessionId(start.sessionId)
          await enterTurn(start.examinerTurn, start.phase)
        } catch (err) {
          if (unmountedRef.current) return
          haptics.error()
          setPhase({ kind: 'error', message: (err as Error).message })
        }
      })()
    }

    return () => {
      // Mark unmounted FIRST so the in-flight bootstrap promise (or any
      // pending playExaminerAudio call) bails out instead of starting a new
      // Audio on a screen that no longer exists.
      unmountedRef.current = true
      if (cueCardTimerRef.current) {
        clearInterval(cueCardTimerRef.current)
        cueCardTimerRef.current = null
      }
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current)
        recordingTimerRef.current = null
      }
      if (audioElRef.current) {
        audioElRef.current.pause()
        audioElRef.current.src = ''
        audioElRef.current = null
      }
      // Defensive: if the user leaves mid-recording, drop the mic stream.
      // recorder.cancel() is idempotent so it's safe to call unconditionally.
      try {
        recorder.cancel()
      } catch {
        // ignore — best-effort teardown
      }
    }
  }, [])

  return {
    phase,
    sessionId,
    lastTranscript,
    partProgress,
    recordingElapsed,
    recordingCap,
    showExaminerText,
    recorderVolume: recorder.volume,
    navigateHome,
    startRecording,
    stopAndUpload,
    retryTurn,
    skipTurn,
    handleClose,
    setShowExaminerText,
  }
}
